import { SystemStyleObject } from '@telescope/cassini-ui';
import { media } from 'utils/styles';
import { ROW_GAP, COLUMN_GAP } from 'utils/constants';
import { BracketSize } from '../providers';

const COLUMNS = {
  MOBILE: 2,
  TABLET: 4,
};

export const stylesheet = ({
  columns,
  currentRoundDisplay,
  voteOptionHeight = 0,
  bracketSize,
}: {
  columns: number;
  currentRoundDisplay: number;
  voteOptionHeight?: number;
  bracketSize: BracketSize;
}): Record<string, SystemStyleObject> => {
  const WINNER_WIDTH = {
    MOBILE: `calc(${((window.innerWidth / COLUMNS.MOBILE + 1) * 100) / window.innerWidth}vw - ${COLUMN_GAP}px)`,
    TABLET: `calc(${((window.innerWidth / COLUMNS.TABLET + 1) * 100) / window.innerWidth}vw - ${COLUMN_GAP}px)`,
    DESKTOP: '0.25fr',
  };
  const getTranslatePosition = (count: number) => {
    const base = ((columns / count) * 100) / columns;
    return `${base * (currentRoundDisplay - 1)}vw`;
  };

  const getSectionWidth = (count: number) => {
    return `${(columns / count) * 100}vw`;
  };

  const quadrantTitlePositions = {
    8: {
      bottomLeft: {
        display: 'none',
      },
      bottomRight: {
        display: 'none',
      },
      topLeft: {
        [media.desktop]: {
          gridColumn: '2',
          gridRow: '3 / span 3',
        },
      },
      topRight: {
        [media.desktop]: {
          gridColumn: '2',
          gridRow: '3 / span 3',
        },
      },
    },
    16: {
      topLeft: {
        [media.desktop]: {
          gridColumn: '2',
          gridRow: '3 / span 3',
        },
      },
      topRight: {
        [media.desktop]: {
          gridColumn: '3',
          gridRow: '3 / span 3',
        },
      },
      bottomLeft: {
        [media.desktop]: {
          gridColumn: '2',
          gridRow: '11 / span 3',
        },
      },
      bottomRight: {
        [media.desktop]: {
          gridColumn: '3',
          gridRow: '11 / span 3',
        },
      },
    },
    32: {
      topLeft: {
        [media.desktop]: {
          gridColumn: '2 / span 2',
          gridRow: '7 / span 3',
        },
      },
      topRight: {
        [media.desktop]: {
          gridColumn: '3 / span 2',
          gridRow: '7 / span 3',
        },
      },
      bottomLeft: {
        [media.desktop]: {
          gridColumn: '2 / span 2',
          gridRow: '23 / span 3',
        },
      },
      bottomRight: {
        [media.desktop]: {
          gridColumn: '3 / span 2',
          gridRow: '23 / span 3',
        },
      },
    },
  };

  return {
    container: {
      display: 'grid',
      alignItems: 'center',
      gridTemplateColumns: `${columns}fr ${WINNER_WIDTH.MOBILE}`,
      gridTemplateAreas: "'region1 winner''region2 winner'",
      gridGap: `${voteOptionHeight + ROW_GAP * 2}px ${COLUMN_GAP}px`,
      overflow: 'hidden',
      padding: `100px ${COLUMN_GAP}px 100px`,
      width: `calc(${getSectionWidth(COLUMNS.MOBILE)} + ${WINNER_WIDTH.MOBILE})`,
      transition: 'transform 0.5s',
      transform: `translateX(calc( -${getTranslatePosition(COLUMNS.MOBILE)} + ${
        (COLUMN_GAP / COLUMNS.MOBILE) * (currentRoundDisplay - 1)
      }px ))`,
      position: 'relative',

      [media.tablet]: {
        gridTemplateColumns: `${columns}fr ${WINNER_WIDTH.TABLET}`,
        gridGap: `${voteOptionHeight + ROW_GAP * 2}px ${COLUMN_GAP}px`,
        width: `calc(${getSectionWidth(COLUMNS.TABLET)} + ${WINNER_WIDTH.TABLET} + ${COLUMN_GAP}px)`,
        transform: `translateX(calc( -${getTranslatePosition(COLUMNS.TABLET)} + ${
          (COLUMN_GAP / COLUMNS.TABLET) * (currentRoundDisplay - 1)
        }px ))`,
      },

      [media.desktop]: {
        paddingTop: '60px',
        width: '100%',
        gridTemplateAreas: "'region1 winner region2'",
        gridTemplateColumns: `1fr ${WINNER_WIDTH.DESKTOP} 1fr`,
        gridGap: COLUMN_GAP,
      },
    },

    grid: {
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gridTemplateRows: 'auto',
      gridGap: `${ROW_GAP}px ${COLUMN_GAP}px`,
      width: '100%',
    },

    region1: {
      gridArea: 'region1',
    },

    region2: {
      gridArea: 'region2',
    },

    winnerContainer: {
      gridArea: 'winner',
      width: '100%',
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',

      [media.desktop]: {
        padding: `0 6%`,
      },

      ':before': {
        content: '""',
        position: 'absolute',
        width: COLUMN_GAP / 2,
        height: 2,
        borderTop: '1px solid var(--cassini-colors-BracketGridVoteOption-border)',
        top: '50%',
        left: 0,
        transform: 'translateX(-100%)',

        [media.desktop]: {
          display: 'none',
        },
      },
    },

    quadrantTitle: {
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 900,
    },

    topLeft: quadrantTitlePositions[bracketSize].topLeft,
    topRight: quadrantTitlePositions[bracketSize].topRight,
    bottomLeft: quadrantTitlePositions[bracketSize].bottomLeft,
    bottomRight: quadrantTitlePositions[bracketSize].bottomRight,
  };
};
